import type { ApolloError } from "@apollo/client";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import {
  exportEntriesToDATEV,
  generateDatevFormatFiles,
} from "@/apis/custom/customRoutes";
import { ErrorMessage } from "@/components/ui/error";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBrowserSessionId } from "@/hooks/useBrowserSessionId";
import { useGetAllDatevTokens } from "@/queries/datev-tokens/get-all";
import { useGetBankBookingsExportGroups } from "@/queries/transactions/export-groups";
import { BankBookingExportHistory } from "./bank-booking-export-history";
import { BankBookingExportMonthSelection } from "./bank-booking-export-month-selection";

type BankBookingsExportPanelProps = {
  bankTransactionIds: string[];
  resetRowSelection: () => void;
  totalBankTransactions: number;
  onOpenExport: (exportId: string) => void;
  selectedExportId?: string;
};

export const BankBookingsExportPanel = ({
  bankTransactionIds,
  resetRowSelection,
  totalBankTransactions,
  onOpenExport,
  selectedExportId,
}: BankBookingsExportPanelProps) => {
  const sessionId = useBrowserSessionId() ?? "";

  const {
    bankTransactionsExportGroups,
    hasMissingDates,
    loading,
    error,
    refetch,
  } = useGetBankBookingsExportGroups({
    exportAll: bankTransactionIds.length === 0,
    bankTransactionIds,
  });

  const { tokens, loading: tokenLoading } = useGetAllDatevTokens({
    fetchPolicy: "network-only",
    skipPermissionsCheck: false,
  });

  const token = tokens?.[0];

  const handleConfirm = async (months: Record<string, string[]>) => {
    await exportEntriesToDATEV({
      entityGroups: months,
      entityType: "bank-transaction",
      sessionId,
    });
  };

  const handleGenerateDatevFormatFiles = async (
    months: Record<string, string[]>,
  ) => {
    await generateDatevFormatFiles({
      entityGroups: months,
      entityType: "bank-transaction",
    });
  };

  return (
    <motion.div
      className="bg-background z-10"
      style={{
        minWidth: "clamp(250px, 23vw, 450px)",
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      <div className="h-full rounded-tl-md border">
        <Content
          token={token}
          loading={loading || tokenLoading}
          error={error}
          refetch={refetch}
          onConfirm={handleConfirm}
          hasMissingDates={hasMissingDates}
          onGenerateDatevFormatFiles={handleGenerateDatevFormatFiles}
          bankTransactionsExportGroups={bankTransactionsExportGroups}
          resetRowSelection={resetRowSelection}
          bankTransactionIds={bankTransactionIds}
          totalBankTransactions={totalBankTransactions}
          onOpenExport={onOpenExport}
          selectedExportId={selectedExportId}
        />
      </div>
    </motion.div>
  );
};

const Content = ({
  token,
  loading,
  error,
  refetch,
  onConfirm,
  hasMissingDates,
  onGenerateDatevFormatFiles,
  bankTransactionsExportGroups,
  resetRowSelection,
  bankTransactionIds,
  totalBankTransactions,
  onOpenExport,
  selectedExportId,
}: {
  token: any | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
  onConfirm: (months: Record<string, string[]>) => Promise<void>;
  onGenerateDatevFormatFiles: (
    months: Record<string, string[]>,
  ) => Promise<void>;
  hasMissingDates: boolean | string[];
  bankTransactionsExportGroups: Record<string, string[]>;
  resetRowSelection: () => void;
  bankTransactionIds: string[];
  totalBankTransactions: number;
  onOpenExport: (exportId: string) => void;
  selectedExportId?: string;
}) => {
  const { t } = useTranslation();

  if (error) return <ErrorMessage message={error.message} retry={refetch} />;

  return (
    <div className="flex flex-col h-full">
      <Tabs defaultValue="export" className="flex flex-col h-full">
        <div className="px-6 pt-4">
          <TabsList>
            <TabsTrigger value="export" className="flex-1">
              {t("transactions.exports.tabs.export")}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1">
              {t("transactions.exports.tabs.history")}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="export"
          className="data-[state=active]:flex-1 flex flex-col overflow-hidden mt-0"
        >
          <BankBookingExportMonthSelection
            loading={loading}
            error={error}
            refetch={refetch}
            hasMissingDates={hasMissingDates}
            token={token}
            bankTransactionIds={bankTransactionIds}
            bankTransactionsExportGroups={bankTransactionsExportGroups}
            onConfirm={onConfirm}
            onGenerateDatevFormatFiles={onGenerateDatevFormatFiles}
            resetRowSelection={resetRowSelection}
            totalBankTransactions={totalBankTransactions}
          />
        </TabsContent>
        <TabsContent
          value="history"
          className="data-[state=active]:flex-1 flex flex-col overflow-hidden mt-0 px-6"
        >
          <BankBookingExportHistory
            onOpenExport={onOpenExport}
            selectedExportId={selectedExportId}
            onRetryExport={onConfirm}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
